/**
 * 路由 · 壳层导航
 * ------------------------------------------------------------
 * 底座路由（认证/聊天/我的/建设中）在此静态定义；
 * 可插拔业务模块路由由模块注册表聚合（见 shell/registry、各 features/*\/module.ts）。
 * 模块在 modules.config.ts 登记 + VITE_EMA_MODULES 白名单控制启停：
 * 未启用模块不注册路由，访问其地址落到「页面不存在」。
 */
import { createRouter, createWebHashHistory, type RouteRecordRaw } from 'vue-router'
import { openTab, titleFor } from '@/domain/worktabs'
import { authBoot, authUser, ensureAuthLoaded, isLoggedIn, mustChangePassword, needsTenantSelection } from '@/data/api/auth-store'
import { menuRepo } from '@/data/api/menu-repository'
import { useToast } from '@/design/ui/UToast'
import { collectModuleRoutes } from '@/shell/registry'

/* 固定首页：历史个人偏好不覆盖根入口、登录和返回首页。 */
export function defaultHome(): string {
  return '/chat'
}

/* 底座路由：登录认证 + 聊天工作台 + 我的 + 建设中占位（菜单 stub 目标），永驻不随模块开关 */
const baseRoutes: RouteRecordRaw[] = [
  { path: '/', redirect: () => defaultHome() },
  { path: '/login', name: 'login', component: () => import('@/features/auth/LoginView.vue'), meta: { bare: true, public: true } },
  { path: '/change-password', name: 'changePassword', component: () => import('@/features/auth/ChangePasswordView.vue'), meta: { bare: true } },
  { path: '/select-tenant', name: 'selectTenant', component: () => import('@/features/auth/TenantSelectView.vue'), meta: { bare: true } },
  { path: '/chat', name: 'chat', component: () => import('@/features/workbench/WorkbenchPage.vue'), meta: { tab: 'chat' } },
  { path: '/profile', name: 'profile', component: () => import('@/features/profile/ProfilePage.vue'), meta: { tab: 'mine' } },
  { path: '/building', name: 'building', component: () => import('@/features/building/BuildingView.vue'), meta: { title: '建设中' } },
  /* 服务暂不可用（登录态无法确认时的重试页）；公开路由，守卫特判防循环 */
  { path: '/unavailable', name: 'unavailable', component: () => import('@/features/errors/ServiceUnavailable.vue'), meta: { bare: true, public: true, title: '服务暂不可用' } },
  /* 平台配置（系统管理/平台管理）：属底座能力，永驻不随业务模块开关（用户 2026-10-31 决定并入底座）。
     meta.perm 与菜单叶子 permission_code 对齐：无权限访问直接回首页（菜单本身已按权限过滤不下发）。
     平台管理（租户/参数）仅平台管理员可见可用；系统管理其余页面向租户管理员开放。 */
  { path: '/sys/users', name: 'sysUsers', component: () => import('@/features/sysadmin/UserView.vue'), meta: { title: '用户管理', perm: 'user.view' } },
  { path: '/sys/accounts', name: 'sysAccounts', component: () => import('@/features/sysadmin/AccountView.vue'), meta: { title: '账号管理', perm: 'account.view' } },
  { path: '/sys/roles', name: 'sysRoles', component: () => import('@/features/sysadmin/RoleView.vue'), meta: { title: '角色管理', perm: 'role.view' } },
  { path: '/sys/perms', name: 'sysPerms', component: () => import('@/features/sysadmin/PermView.vue'), meta: { title: '权限管理', perm: 'func.view' } },
  { path: '/sys/tenants', name: 'sysTenants', component: () => import('@/features/sysadmin/TenantView.vue'), meta: { title: '租户管理', perm: 'tenant.view' } },
  { path: '/sys/audit', name: 'sysAudit', component: () => import('@/features/sysadmin/AuditView.vue'), meta: { title: '审计日志' } },
  { path: '/sys/notices', name: 'sysNotices', component: () => import('@/features/sysadmin/NoticeView.vue'), meta: { title: '公告管理', perm: 'sys.view' } },
  { path: '/sys/params', redirect: '/sys/config' },
  { path: '/sys/depts', name: 'sysDepts', component: () => import('@/features/sysadmin/DeptView.vue'), meta: { title: '部门管理', perm: 'dept.view' } },
  { path: '/sys/posts', name: 'sysPosts', component: () => import('@/features/sysadmin/PostView.vue'), meta: { title: '岗位管理', perm: 'post.view' } },
  { path: '/sys/menus', name: 'sysMenus', component: () => import('@/features/sysadmin/MenuView.vue'), meta: { title: '菜单管理', perm: 'menu.view' } },
  { path: '/sys/plans', name: 'sysPlans', component: () => import('@/features/sysadmin/PlanView.vue'), meta: { title: '套餐定义', perm: 'plan.view' } },
  { path: '/sys/codes', name: 'sysCodes', component: () => import('@/features/sysadmin/CodeView.vue'), meta: { title: '激活码管理', perm: 'code.edit' } },
  { path: '/sys/invite-relations', name: 'sysInviteRelations', component: () => import('@/features/sysadmin/InviteRelationView.vue'), meta: { title: '邀请关系', perm: 'code.view' } },
  { path: '/sys/dict', name: 'sysDict', component: () => import('@/features/sysadmin/DictView.vue'), meta: { title: '字典管理', perm: 'dict.view' } },
  { path: '/sys/config', name: 'sysConfig', component: () => import('@/features/sysadmin/ConfigView.vue'), meta: { title: '参数管理', perm: 'config.view' } },
  { path: '/sys/tools', name: 'sysTools', component: () => import('@/features/sysadmin/ToolsView.vue'), meta: { title: 'AI 工具管理', perm: 'config.view' } },
  { path: '/sys/ai/calls', name: 'sysToolCalls', component: () => import('@/features/sysadmin/ToolCallsView.vue'), meta: { title: 'AI 调用审计', perm: 'config.view' } },
  { path: '/sys/logs', name: 'sysLogs', component: () => import('@/features/sysadmin/LogView.vue'), meta: { title: '日志管理', perm: 'log.view' } },
  { path: '/sys/online', name: 'sysOnline', component: () => import('@/features/sysadmin/OnlineView.vue'), meta: { title: '在线用户', perm: 'online.view' } },
  { path: '/sys/versions', name: 'sysVersions', component: () => import('@/features/sysadmin/VersionView.vue'), meta: { title: '版本管理', perm: 'version.view' } },
  // 租户配置：基本信息维护（本租户公司名称/简称/Logo/邮箱服务器）
  { path: '/tenant/profile', name: 'tenantProfile', component: () => import('@/features/tenant/TenantProfileView.vue'), meta: { title: '基本信息维护', perm: 'tenant.profile.view' } },
]

const routes: RouteRecordRaw[] = [
  ...baseRoutes,
  ...collectModuleRoutes(),
  { path: '/:pathMatch(.*)*', name: 'notFound', component: () => import('@/features/errors/NotFound.vue'), meta: { title: '页面不存在' } }
]

const router = createRouter({
  /* hash base 跟随 Vite base（默认 /），URL 形如 http://localhost:5173/#/chat */
  history: createWebHashHistory(import.meta.env.BASE_URL),
  routes
})

// 全站登录守卫：除公开路由外，未登录一律回登录页；首登未改密先改密。
router.beforeEach(async to => {
  if (to.meta.public === true) return true

  await ensureAuthLoaded()
  if (!isLoggedIn.value) {
    /* 服务暂不可用（网络错误/5xx）≠ 未登录：进重试页，恢复后回原目标 */
    if (authBoot.value === 'unavailable') {
      return { path: '/unavailable', query: to.fullPath === '/' ? {} : { redirect: to.fullPath } }
    }
    return { path: '/login', query: to.fullPath === '/' ? {} : { redirect: to.fullPath } }
  }
  if (mustChangePassword.value && to.path !== '/change-password') return '/change-password'
  if (!mustChangePassword.value && to.path === '/change-password') return defaultHome()

  /* 多租户：会话未绑定租户一律先选租户；已绑定（或无需绑定）时选择页回首页 */
  if (needsTenantSelection.value && to.path !== '/select-tenant') return '/select-tenant'
  if (!needsTenantSelection.value && to.path === '/select-tenant') return defaultHome()

  /* 功能权限：meta.perm 页面（平台/系统管理）仅持对应权限点或超管('*')可进入，否则回首页 */
  const needPerm = to.meta.perm as string | undefined
  if (needPerm) {
    const perms = authUser.value?.perms ?? []
    if (!perms.includes('*') && !perms.includes(needPerm)) return defaultHome()
  }

  /* 套餐门控（硬门控，2026-11-01 决定）：会话租户套餐在 plan_menu 有绑定时，
     菜单可见性裁剪之外，直达被裁菜单的 URL 也拦回首页并提示；
     allowed=null（空绑定）不限制。scope 拉取失败不阻断导航（菜单接口会显错）。 */
  try {
    const scope = await menuRepo.scope()
    if (scope.allowed && scope.known.includes(to.path) && !scope.allowed.includes(to.path)) {
      useToast().show('当前套餐未开通该功能，请联系管理员')
      return defaultHome()
    }
  } catch { /* 忽略门控数据拉取失败，不阻断导航 */ }
})

/* 工作页签：任何导航都会同步到顶部标签栏（openTab 幂等：fullPath 相同仅激活；query 不同各成一签） */
router.afterEach(to => { openTab(to.fullPath, titleFor(to.fullPath, to.meta.title as string | undefined)) })

export default router
